import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Dropdown,
    ReadOnlyGuard,
    SparkIcon,
    getOverlayPortalParent,
    useAnchoredPanelPosition,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import { useCanManageProviders } from '@/features/auth/providerAccess';

const QUOTA_ITEMS = [
    { label: "Increase the deployment's TPM quota", action: 'openQuotaLink' },
    { label: 'Add Azure Global-Batch deployment', action: 'openBatchFix' },
];

const CANCEL_ITEM = { label: 'Cancel evaluation', action: 'cancel', disableWhenReadOnly: true };

// Settings deep link for the API-key (OpenAI Resource) batch fix: Model
// Providers tab -> AzureAI -> OpenAI Resource, focused on the
// Global-Batch Deployments input.
const BATCH_SETTINGS_LINK = '/settings?tab=providers&provider=azure&focus=batch-deployments';

/**
 * AlertBanner "Fix" pill (Figma 5190:12013): opens a Spark Dropdown with
 * quota-remediation links and cancel. The TPM-quota option opens the
 * provider deployments URL (`quotaLink`). The batch-deployment option
 * depends on `azureMode`: a Foundry project manages batch deployments in
 * the same portal page (`quotaLink` again), while a classic OpenAI
 * resource registers them on the in-app settings page, so it deep-links
 * there instead. When cancelling is the only available action, the
 * dropdown collapses into a plain "Cancel" button.
 */
const TeacherEvaluationFixMenu = ({ quotaLink, azureMode, onCancel }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const close = useCallback(() => setOpen(false), []);

    useOutsideClick([wrapRef, panelRef], close, open);
    useEscapeKey(close, open);
    const panelStyle = useAnchoredPanelPosition(wrapRef, panelRef, open, 'end');

    // SaaS non-gods cannot open Model Providers (the keys are SpecificAI's),
    // so the in-app batch-deployment fix is dropped for them.
    const canManageProviders = useCanManageProviders();
    const items = useMemo(() => {
        const next = [];
        if (quotaLink) {
            next.push(
                ...QUOTA_ITEMS.filter(
                    (item) => item.action !== 'openBatchFix' || azureMode !== 'openai' || canManageProviders
                )
            );
        }
        if (typeof onCancel === 'function') next.push(CANCEL_ITEM);
        return next;
    }, [quotaLink, onCancel, azureMode, canManageProviders]);

    if (items.length === 0) return null;

    if (items.length === 1 && items[0].action === 'cancel') {
        return (
            <div className="quality-metrics-fix-menu">
                <ReadOnlyGuard>
                    <button
                        type="button"
                        className="spark-alert-banner-action"
                        onClick={() => onCancel()}
                    >
                        Cancel
                    </button>
                </ReadOnlyGuard>
            </div>
        );
    }

    const handleSelect = (item) => {
        close();
        const action = items.find((option) => option.label === item.label)?.action;
        if (action === 'openQuotaLink' && quotaLink) {
            window.open(quotaLink, '_blank', 'noopener,noreferrer');
            return;
        }
        if (action === 'openBatchFix') {
            if (azureMode === 'openai') {
                navigate(BATCH_SETTINGS_LINK, {
                    state: { from: `${location.pathname}${location.search}${location.hash}` },
                });
            } else if (quotaLink) {
                window.open(quotaLink, '_blank', 'noopener,noreferrer');
            }
            return;
        }
        if (action === 'cancel') onCancel?.();
    };

    const panel = open ? (
        <div ref={panelRef} className="quality-metrics-fix-menu-panel" style={panelStyle}>
            <Dropdown
                type="regular"
                showTitle={false}
                showFooter={false}
                maxVisibleItems={items.length}
                items={items.map(({ label, disableWhenReadOnly }) => ({ label, disableWhenReadOnly }))}
                onSelect={handleSelect}
            />
        </div>
    ) : null;

    return (
        <div ref={wrapRef} className="quality-metrics-fix-menu">
            <button
                type="button"
                className="spark-alert-banner-action"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((current) => !current)}
            >
                Fix
                <SparkIcon slug={open ? 'chevron-up' : 'chevron-down'} size="small" />
            </button>
            {open ? createPortal(panel, getOverlayPortalParent(wrapRef.current)) : null}
        </div>
    );
};

export default TeacherEvaluationFixMenu;
